// This uses the Mapbox GL library.
// For docs, see https://www.mapbox.com/mapbox-gl-js/api/
import mapboxgl from "mapbox-gl"
import "mapbox-gl/dist/mapbox-gl.css"
import turf from "turf"
import _isEmpty from "lodash/isEmpty"

export default class DynamicMap {
  // Public: Create cluster maps.
  //
  // elements - Array of DOM elements having these attributes:
  //            data-url          - URL of GeoJSON point data
  //            data-access-token - Mapbox access token
  //
  // Returns nothing.
  static initClusterMaps(elements) {
    for (let element of elements) {
      $.getJSON(element.dataset.dataUrl, data => {
        if (!_isEmpty(data.features)) {
          let map = new DynamicMap(element.dataset.accessToken, element)
          map.addTrailClusters(data)
        }
      })
    }
  }

  // Public: Initialize a map.
  //
  // accessToken - Mapbox access token String
  // container   - Map container DOM element
  // mapOptions  - Object that will be passed to `mapboxgl.Map` constructor (optional)
  //
  // Returns a map instance.
  constructor(accessToken, container, mapOptions) {
    mapboxgl.accessToken = accessToken
    this._unclusteredPointsLayer = "unclustered-point"
    this._clustersLayer = "clusters"
    this._trailSource = "trails"
    this._trailMap = new mapboxgl.Map({
      container: container,
      style: "mapbox://styles/mapbox/outdoors-v10",
      scrollZoom: false,
      ...mapOptions,
    })
    this._pointPopup = new mapboxgl.Popup({
      closeButton: false,
      closeOnClick: false,
    })
    this._disableTouchPan()
  }

  get center() {
    return this._trailMap.getCenter().toArray()
  }

  get zoom() {
    return this._trailMap.getZoom()
  }

  get data() {
    return this._data
  }

  get accessToken() {
    return mapboxgl.accessToken
  }

  onRender(callback) {
    this._trailMap.on("render", callback)
  }

  onLoad(callback) {
    this._trailMap.on("load", callback)
  }

  addTrailPath(data, id = "trail") {
    if (_isEmpty(data)) {
      return
    }
    this._data = data
    this._trailMap.addLayer({
      id: id,
      type: "line",
      source: {
        type: "geojson",
        data,
      },
    })
    this._fitGeo(data)
  }

  clearTrailPath(id = "trail") {
    if (this._trailMap.getLayer(id)) {
      this._trailMap.removeLayer(id)
    }
    if (this._trailMap.getSource(id)) {
      this._trailMap.removeSource(id)
    }
  }

  // Public: Add trail clusters to the map.
  //
  // This sets up the data source, layers, and hover/click events.
  //
  // data - GeoJSON Object or URL String
  //
  // Returns nothing.
  addTrailClusters(data) {
    this._data = data
    this._trailMap.on("load", () => {
      this._trailMap.addSource(this._trailSource, {
        type: "geojson",
        data: data,
        cluster: true,
        clusterMaxZoom: 14,
      })
      this._fitGeo(data, { maxZoom: 10 })
      this._addTrailClusterLayers()
      this._bindClustersLayerEvents()
      this._bindPointsLayerEvents()
    })
  }

  // Internal: Adjust map zoom to fit features.
  //
  // geoJson - Valid GeoJSON object
  // options - Object with extra options to pass to `fitBounds` (optional)
  //
  // Returns nothing.
  _fitGeo(geoJson, options) {
    let bounds = turf.bbox(geoJson)
    let defaultOptions = { padding: 50, speed: 3 }
    this._trailMap.fitBounds(bounds, { ...defaultOptions, ...options })
  }

  // Internal: Bind events on clusters layer. This includes zoom on click and
  // changing the cursor style on hover.
  //
  // Returns nothing.
  _bindClustersLayerEvents() {
    this._trailMap.on(
      "click",
      this._clustersLayer,
      this._zoomToCluster.bind(this)
    )
    this._trailMap.on(
      "mouseenter",
      this._clustersLayer,
      this._cursorPointer.bind(this)
    )
    this._trailMap.on(
      "mouseleave",
      this._clustersLayer,
      this._cursorDefault.bind(this)
    )
  }

  // Internal: Bind events on unclustered points layer. These include clicking a
  // point to navigate to its URL and changing the cursor style on hover.
  //
  // Returns nothing.
  _bindPointsLayerEvents() {
    this._trailMap.on(
      "click",
      this._unclusteredPointsLayer,
      this._navigateToPointUrl
    )
    this._trailMap.on(
      "mouseenter",
      this._unclusteredPointsLayer,
      this._showPointPopup.bind(this)
    )
    this._trailMap.on(
      "mouseleave",
      this._unclusteredPointsLayer,
      this._hidePointPopup.bind(this)
    )
  }

  // Internal: Prevent touch events from panning the map. This makes it easier
  // to scroll the page on mobile devices.
  //
  // Returns nothing.
  _disableTouchPan() {
    this._trailMap.on("touchstart", () => {
      this._trailMap.dragPan.disable()
    })
    this._trailMap.on("touchend", () => {
      this._trailMap.dragPan.enable()
    })
    this._trailMap.on("touchcancel", () => {
      this._trailMap.dragPan.enable()
    })
  }

  // Internal: Add cluster layers to the map.
  //
  // This creates three layers:
  //   (1) Cluster circles (sized according to the number of points in the cluster)
  //   (2) Cluster text (count of points in the cluster)
  //   (3) Non-clustered point circles
  //
  // Returns nothing.
  _addTrailClusterLayers() {
    this._trailMap.addLayer({
      id: this._clustersLayer,
      type: "circle",
      source: this._trailSource,
      filter: ["has", "point_count"],
      paint: {
        "circle-color": "#39a7c8",
        "circle-radius": ["step", ["get", "point_count"], 12, 10, 20],
      },
    })

    this._trailMap.addLayer({
      id: "cluster-count",
      type: "symbol",
      source: this._trailSource,
      filter: ["has", "point_count"],
      layout: {
        "text-field": "{point_count_abbreviated}",
        "text-size": 12,
      },
      paint: {
        "text-color": "#fff",
      },
    })

    this._trailMap.addLayer({
      id: "unclustered-point",
      type: "circle",
      source: this._trailSource,
      filter: ["!has", "point_count"],
      paint: {
        "circle-radius": 8,
        "circle-color": "#39a7c8",
        "circle-stroke-width": 1,
        "circle-stroke-color": "#fff",
      },
    })
  }

  // Internal: Set the cursor style to pointer.
  //
  // Returns nothing.
  _cursorPointer() {
    this._trailMap.getCanvas().style.cursor = "pointer"
  }

  // Internal: Set the cursor style to the default.
  //
  // Returns nothing.
  _cursorDefault() {
    this._trailMap.getCanvas().style.cursor = ""
  }

  // Internal: Zoom to a cluster.
  //
  // point - Mapbox point
  //
  // Returns nothing.
  _zoomToCluster({ point }) {
    let features = this._trailMap.queryRenderedFeatures(point, {
      layers: [this._clustersLayer],
    })
    let clusterId = features[0].properties.cluster_id
    this._trailMap
      .getSource(this._trailSource)
      .getClusterExpansionZoom(clusterId, (err, zoom) => {
        if (err) {
          return
        }
        this._trailMap.easeTo({
          center: features[0].geometry.coordinates,
          zoom: zoom,
        })
      })
  }

  // Internal: Show popup for an individual point. Popup includes trail name.
  //
  // features - Array of GeoJSON features
  //
  // Returns nothing.
  _showPointPopup({ features }) {
    this._cursorPointer()
    let coordinates = features[0].geometry.coordinates.slice()
    let name = features[0].properties.name
    this._pointPopup
      .setLngLat(coordinates)
      .setHTML(name)
      .addTo(this._trailMap)
  }

  // Internal: Hide point popup.
  //
  // Returns nothing.
  _hidePointPopup() {
    this._cursorDefault()
    this._pointPopup.remove()
  }

  // Internal: Navigate to the URL for an individual point.
  //
  // features - Array of GeoJSON features
  //
  // Returns nothing.
  _navigateToPointUrl({ features }) {
    window.location = features[0].properties.url
  }
}
